import { ExternalLink } from "../../components/ui";
import { IconCheck } from "../../components/icons";
import { useI18n } from "../../i18n";
import { chainInfo, explorerTxUrl } from "../../lib/chains";
import { shortHash } from "../../lib/money";
import type { PublicInvoice } from "../../lib/types";

/** Between "the wallet returned a signature" (or a transfer was seen) and a final status. */
export function ProgressView({ invoice, submittedTx }: { invoice: PublicInvoice; submittedTx: string | null }) {
  const { t } = useI18n();
  const chain = chainInfo(invoice.asset.chain);
  const seen = invoice.status === "confirming";
  const txHash = invoice.txHash ?? submittedTx;
  const fromWallet = submittedTx !== null || invoice.paidMode === "wallet";

  const steps = [
    { label: fromWallet ? t("progress.sent") : t("progress.detected", { chain: chain.name }), state: "done" as const },
    ...(fromWallet ? [{ label: t("progress.detected", { chain: chain.name }), state: seen ? ("done" as const) : ("active" as const) }] : []),
    { label: t("progress.confirming"), state: seen ? ("active" as const) : ("todo" as const) },
    { label: t("progress.paid"), state: "todo" as const },
  ];

  return (
    <section className="progress" aria-live="polite">
      <div className="progress-head">
        <span className="progress-orb" aria-hidden="true">
          <span className="progress-orb-ring" />
        </span>
        <div>
          <h2 className="h2">{seen ? t("progress.confirming") : t("progress.title")}</h2>
          <p className="muted">{t("progress.confirmingHint")}</p>
        </div>
      </div>

      <div className="progress-bar" role="progressbar" aria-label={t("progress.confirming")}>
        <span className="progress-bar-fill" style={{ width: seen ? "72%" : "38%" }} />
        <span className="progress-bar-shimmer" />
      </div>

      <ol className="steps">
        {steps.map((s) => (
          <li key={s.label} className={`step step-${s.state}`}>
            <span className="step-mark" aria-hidden="true">
              {s.state === "done" ? <IconCheck size={13} strokeWidth={2.5} /> : null}
            </span>
            <span className="step-label">{s.label}</span>
          </li>
        ))}
      </ol>

      {txHash ? (
        <div className="progress-tx">
          <span className="muted small">{t("progress.tx")}</span>
          <ExternalLink href={invoice.explorerUrl ?? explorerTxUrl(invoice.asset.chain, txHash)}>
            <span className="mono">{shortHash(txHash, 8, 8)}</span>
          </ExternalLink>
        </div>
      ) : null}
    </section>
  );
}
